/**
 * Parent-owned drag machine (spec §5.4). Native DnD cannot cross the
 * iframe document boundary, so the shell drives every drag itself:
 * pointerdown on a source arms a pending drag, a 4px move activates it,
 * a transparent capture layer keeps the iframe from swallowing pointer
 * events, and moves are routed to one of two drop surfaces — the canvas
 * (translated coordinates streamed to the bridge, which hit-tests and
 * draws indicators) or the outline (row-rect hit-testing in the parent
 * DOM). The drop commits exactly one store mutation (one undo step).
 */

import { inject, provide, ref, watch } from 'vue';
import type { InjectionKey, Ref } from 'vue';
import type { DropTarget } from './protocol';
import type { BlockNode } from './types';
import { allowedTargetsFor } from './nestingRules';
import type { BuilderStore } from './useBuilderStore';
import type { BuilderBridge } from './useBuilderBridge';
import type { BuilderRegistry } from './useBuilderRegistry';
import { sharedBlockNode } from './useBlockInsertion';
import type { PaletteHistory } from './usePaletteHistory';

export type DragPayload =
    | { kind: 'move'; blockId: string }
    | { kind: 'new'; typeSlug: string }
    | { kind: 'new-shared'; blockId: string | number };

/** A parent-DOM drop surface (the outline panel). */
export interface OutlineSurface {
    /** Hit-test a pointer position; draws its own row indicator. */
    hitTest(clientX: number, clientY: number, dragged: BlockNode[]): DropTarget | null;
    clearIndicator(): void;
}

export interface DragMachine {
    dragging: Ref<boolean>;
    ghost: Ref<{ x: number; y: number; label: string } | null>;
    beginPointerDrag(event: PointerEvent, payload: DragPayload, label: string): void;
    registerOutlineSurface(surface: OutlineSurface | null): void;
}

const MACHINE_KEY: InjectionKey<DragMachine> = Symbol('builder-v2-drag');

const DRAG_THRESHOLD_PX = 4;
const EDGE_ZONE_PX = 60;
const EDGE_STEP_PX = 16;
const DROP_TIMEOUT_MS = 300;

export function createDragMachine(
    store: BuilderStore,
    bridge: BuilderBridge,
    registry: BuilderRegistry,
    history?: PaletteHistory,
): DragMachine {
    const dragging = ref(false);
    const ghost = ref<{ x: number; y: number; label: string } | null>(null);

    let pending: { x: number; y: number; payload: DragPayload; label: string } | null = null;
    let active: { payload: DragPayload; outlineTarget: DropTarget | null; overCanvas: boolean } | null = null;
    let outlineSurface: OutlineSurface | null = null;
    let lastClient = { x: 0, y: 0 };
    let moveRaf: number | null = null;
    let queuedMove: { x: number; y: number } | null = null;
    let scrollDirection = 0;
    let scrollRaf: number | null = null;

    const draggedBlocks = (payload: DragPayload): BlockNode[] => {
        if (payload.kind === 'new') {
            return [{ type: payload.typeSlug }];
        }

        if (payload.kind === 'new-shared') {
            return [sharedBlockNode(payload.blockId)];
        }

        const block = store.findBlock(payload.blockId);

        return block ? [block] : [];
    };

    /** rAF-throttled dragMove stream to the bridge. */
    const queueDragMove = (x: number, y: number): void => {
        queuedMove = { x, y };

        if (moveRaf !== null) {
            return;
        }

        moveRaf = requestAnimationFrame(() => {
            moveRaf = null;

            if (queuedMove !== null && active !== null) {
                bridge.dragMove(queuedMove.x, queuedMove.y);
                queuedMove = null;
            }
        });
    };

    /** Route the pointer to the canvas or outline surface. */
    const routeMove = (clientX: number, clientY: number): void => {
        if (active === null) {
            return;
        }

        const frameRect = bridge.iframe.value?.getBoundingClientRect();
        const overCanvas =
            frameRect !== undefined &&
            clientX >= frameRect.left &&
            clientX <= frameRect.right &&
            clientY >= frameRect.top &&
            clientY <= frameRect.bottom;

        if (overCanvas && frameRect) {
            if (!active.overCanvas) {
                active.overCanvas = true;
                active.outlineTarget = null;
                outlineSurface?.clearIndicator();
            }

            // frameRect is the post-transform (scaled) box, the bridge
            // hit-tests unscaled iframe coordinates — divide by the scale
            const scale = store.canvasScale.value || 1;
            queueDragMove((clientX - frameRect.left) / scale, (clientY - frameRect.top) / scale);
            return;
        }

        if (active.overCanvas) {
            active.overCanvas = false;
            queueDragMove(-1, -1);
        }

        active.outlineTarget = outlineSurface?.hitTest(clientX, clientY, draggedBlocks(active.payload)) ?? null;

        if (active.outlineTarget === null) {
            outlineSurface?.clearIndicator();
        }
    };

    /** Parent-side edge auto-scroll; the iframe itself never scrolls. */
    const updateEdgeScroll = (clientY: number): void => {
        if (clientY < EDGE_ZONE_PX) {
            scrollDirection = -1;
        } else if (clientY > window.innerHeight - EDGE_ZONE_PX) {
            scrollDirection = 1;
        } else {
            scrollDirection = 0;
        }

        if (scrollDirection !== 0 && scrollRaf === null) {
            const step = (): void => {
                scrollRaf = null;

                if (scrollDirection === 0 || active === null) {
                    return;
                }

                window.scrollBy(0, scrollDirection * EDGE_STEP_PX);
                // the iframe rect shifted — recompute routing from the last pointer
                routeMove(lastClient.x, lastClient.y);
                scrollRaf = requestAnimationFrame(step);
            };

            scrollRaf = requestAnimationFrame(step);
        }
    };

    const begin = (event: PointerEvent): void => {
        if (pending === null) {
            return;
        }

        const blocks = draggedBlocks(pending.payload);

        if (blocks.length === 0) {
            cleanup();
            return;
        }

        dragging.value = true;
        active = { payload: pending.payload, outlineTarget: null, overCanvas: false };
        ghost.value = { x: event.clientX, y: event.clientY, label: pending.label };
        document.body.style.userSelect = 'none';

        // the bridge protocol only distinguishes new vs move — a shared
        // block drag is geometrically a 'new' drag
        bridge.dragStart(
            pending.payload.kind === 'move' ? 'move' : 'new',
            pending.payload.kind === 'move' ? [pending.payload.blockId] : [],
            allowedTargetsFor(store.content.value, blocks),
        );
    };

    const onPointerMove = (event: PointerEvent): void => {
        lastClient = { x: event.clientX, y: event.clientY };

        if (!dragging.value) {
            if (pending === null) {
                return;
            }

            if (Math.hypot(event.clientX - pending.x, event.clientY - pending.y) < DRAG_THRESHOLD_PX) {
                return;
            }

            begin(event);
        }

        if (active === null) {
            return;
        }

        if (ghost.value !== null) {
            ghost.value = { ...ghost.value, x: event.clientX, y: event.clientY };
        }

        routeMove(event.clientX, event.clientY);
        updateEdgeScroll(event.clientY);
    };

    /** Await the bridge's authoritative drop reply, with an echo fallback. */
    const awaitDrop = (): Promise<DropTarget | null> => {
        const baseline = bridge.lastDrop.value ? bridge.lastDrop.value.serial : 0;

        return new Promise((resolve) => {
            const timer = setTimeout(() => {
                stopWatch();
                resolve(bridge.dropTarget.value);
            }, DROP_TIMEOUT_MS);

            const stopWatch = watch(bridge.lastDrop, (drop) => {
                if (drop && drop.serial > baseline) {
                    clearTimeout(timer);
                    stopWatch();
                    resolve(drop.target);
                }
            });
        });
    };

    const commitDrop = (payload: DragPayload, target: DropTarget): void => {
        if (payload.kind === 'move') {
            store.moveTo(payload.blockId, target.parentId, target.slot, target.beforeId);
            return;
        }

        if (payload.kind === 'new-shared') {
            if (store.insertAt(sharedBlockNode(payload.blockId), target.parentId, target.slot, target.beforeId)) {
                history?.pushRecent({ kind: 'shared', key: String(payload.blockId) });
            }

            return;
        }

        const inserted = store.insertAt(
            {
                type: payload.typeSlug,
                useBlock: false,
                value: registry.defaultValueFor(payload.typeSlug),
                settings: {},
            },
            target.parentId,
            target.slot,
            target.beforeId,
        );

        if (inserted !== null) {
            history?.pushRecent({ kind: 'type', key: payload.typeSlug });
        }
    };

    const onPointerUp = (): void => {
        if (!dragging.value || active === null) {
            cleanup();
            return;
        }

        const finished = active;

        if (finished.overCanvas) {
            bridge.dragEnd();
            void awaitDrop().then((target) => {
                if (target !== null) {
                    commitDrop(finished.payload, target);
                }
            });
        } else {
            bridge.dragCancel();

            if (finished.outlineTarget !== null) {
                commitDrop(finished.payload, finished.outlineTarget);
            }
        }

        cleanup();
    };

    const onKeydown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') {
            bridge.dragCancel();
            cleanup();
        }
    };

    const cancel = (): void => {
        bridge.dragCancel();
        cleanup();
    };

    function cleanup(): void {
        if (dragging.value) {
            // a real drag happened — swallow the click the same pointerup
            // fires, so click-insert sources do not double-trigger
            window.addEventListener(
                'click',
                (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                },
                { capture: true, once: true },
            );
        }

        dragging.value = false;
        ghost.value = null;
        pending = null;
        active = null;
        queuedMove = null;
        scrollDirection = 0;
        document.body.style.userSelect = '';
        outlineSurface?.clearIndicator();
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('pointercancel', cancel);
        window.removeEventListener('keydown', onKeydown, true);
    }

    const machine: DragMachine = {
        dragging,
        ghost,
        beginPointerDrag(event, payload, label) {
            if (store.mode.value === 'readonly' || event.button !== 0) {
                return;
            }

            pending = { x: event.clientX, y: event.clientY, payload, label };
            lastClient = { x: event.clientX, y: event.clientY };
            window.addEventListener('pointermove', onPointerMove);
            window.addEventListener('pointerup', onPointerUp);
            window.addEventListener('pointercancel', cancel);
            window.addEventListener('keydown', onKeydown, true);
        },
        registerOutlineSurface(surface) {
            outlineSurface = surface;
        },
    };

    provide(MACHINE_KEY, machine);

    return machine;
}

export function useDragMachine(): DragMachine {
    const machine = inject(MACHINE_KEY);

    if (!machine) {
        throw new Error('useDragMachine must be used inside a BuilderV2 tree');
    }

    return machine;
}
